import { isValid } from 'date-fns'
import Kontonummer from 'kontonummer'
import i18n from '@/plugins/i18n'

// Generic, view-agnostic formatters ported from ada-app-ui.

export function getIntlLocales(): string[] {
  return [i18n.global.locale.value, 'sv-SE']
}

/**
 * Format a number as a currency
 * @param value The number to format
 * @param currency The currency to use (default: SEK)
 * @param fractionDigits Optional number of fraction digits. If undefined, uses currency default (typically 2).
 * Use 0 for whole numbers, 2 for standard decimal display.
 * @returns The formatted currency string from Intl.NumberFormat - for example 100 kr
 */
export function formatCurrency(value: number | undefined, currency = 'SEK', fractionDigits?: number): string {
  if (value == null) return ''

  const options: Intl.NumberFormatOptions = { style: 'currency', currency }

  if (fractionDigits != null) {
    options.minimumFractionDigits = fractionDigits
    options.maximumFractionDigits = fractionDigits
  }

  return new Intl.NumberFormat(getIntlLocales(), options).format(value)
}

/**
 * Format a date as a short date string
 * @param value The date to format
 * @returns The formatted date string from Intl.DateTimeFormat - for example 2025-11-27
 */
export function formatDate(value: string | number | Date): string {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (value == null || !isValid(new Date(value))) return ''
  return new Intl.DateTimeFormat(getIntlLocales(), { dateStyle: 'short' }).format(new Date(value))
}

/**
 * Format a date as day + full month name, no year — e.g. "31 juli". For compact human date
 * references where the year is implied (e.g. an upcoming amortization advice).
 * @param value The date to format
 * @returns The formatted day + month, or '' for an invalid/absent value
 */
export function formatDayMonth(value: string | number | Date | null | undefined): string {
  if (value == null || !isValid(new Date(value))) return ''
  return new Intl.DateTimeFormat(getIntlLocales(), { day: 'numeric', month: 'long' }).format(new Date(value))
}

/**
 * Format a date as full month name + year — e.g. "september 2026". For estimated dates where
 * only the month is meaningful (e.g. planned amortization advices).
 * @param value The date to format
 * @returns The formatted month + year, or '' for an invalid/absent value
 */
export function formatMonthYear(value: string | number | Date | null | undefined): string {
  if (value == null || !isValid(new Date(value))) return ''
  return new Intl.DateTimeFormat(getIntlLocales(), { month: 'long', year: 'numeric' }).format(new Date(value))
}

/**
 * Format bytes as human-readable text.
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024. Defaults to true
 * @param dp Number of decimal places to display. Defaults to 1
 *
 * @return Formatted string - for example 4.1 MB
 */
export function formatByteSize(bytes: number, si = true, dp = 1) {
  const thresh = si ? 1000 : 1024

  if (Math.abs(bytes) < thresh) {
    return `${bytes} B`
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  let u = -1
  const r = 10 ** dp

  do {
    bytes /= thresh
    ++u
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)

  return `${bytes.toFixed(dp)} ${units[u]}`
}

/**
 * Truncate a string to a certain length by removing characters from the middle,
 * inserting an ellipsis in its place
 * @param val The string to truncate
 * @param length The length of the string to truncate to
 * @returns The truncated string, for example Hello World could become He…ld
 */
export function truncateCenter(val: string, length: number) {
  if (val.length < length) return val
  const lettersToTake = Math.round((length - 1) / 2)
  return `${val.slice(0, lettersToTake)}…${val.slice(-lettersToTake)}`
}

/**
 * Format an OCR number for display: digit groups of four, left to right, with a short tail
 * (1–2 digits) folded into the last group so no orphan is left dangling.
 * @example formatOcr("01259435790831790") => "0125 9435 7908 31790" (17 digits → 4/4/4/5)
 * @example formatOcr("20261956894870385170") => "2026 1956 8948 7038 5170"
 */
export function formatOcr(value: string | undefined): string | undefined {
  if (value == null || value === '') return value
  const digits = value.replace(/\s/g, '')
  const groups = digits.match(/.{1,4}/g) ?? []
  const tail = groups.at(-1)
  if (groups.length > 1 && tail != null && tail.length <= 2) {
    groups.splice(-2, 2, `${groups.at(-2)}${tail}`)
  }
  return groups.join(' ')
}

/**
 * Format a string with proper case (title case with hyphen support)
 * @param value The string to format
 * @returns The formatted string with:
 *   - First letter of each word capitalized
 *   - First letter after hyphens capitalized
 *   - All other letters lowercased
 * @example formatProperCase("JOHN DOE") => "John Doe"
 * @example formatProperCase("anne-marie smith") => "Anne-Marie Smith"
 */
export function formatProperCase(value: string): string {
  return value
    .toLocaleLowerCase(getIntlLocales())
    .replace(
      /(^|[\s-])(\S)/g,
      (_: string, prefix: string, char: string) => prefix + char.toLocaleUpperCase(getIntlLocales())
    )
}

/** Swedish clearing + account number rendered as one line via the `kontonummer`
 *  library; falls back to "<clearing> <account>" when it can't validate/parse them. */
export function formatBankAccount(clearing: string, account: string): string {
  try {
    return new Kontonummer(clearing, account, { mode: 'strict' }).format('pretty')
  } catch {
    // Expected for accounts the library can't validate/parse (e.g. unusual/foreign).
    return `${clearing} ${account}`.trim()
  }
}
